import {
  ApplicationRef,
  ComponentRef,
  Injectable,
  Type,
  ViewContainerRef
} from '@angular/core';
import { LngLat, LngLatLike, Map, Marker, Popup } from 'maplibre-gl';
import {ObservationsManagerService} from '../../../../data/observations/observations-manager.service';
import {Observation} from '../../../models/observation';
import {ObservationInfoWindowContentComponent} from '../observation-info-window-content/observation-info-window-content.component';
import {ObservationFilterUpdate} from '../../../models/observations-filter';
import {Subscription} from 'rxjs';
import {RoadStatusInfoWindowContentComponent} from '../road-status-info-window-content/road-status-info-window-content.component';
import {AlertInfoWindowContentComponent} from '../alert-info-window-content/alert-info-window-content.component';
import {IncidentInfo} from './traffic-layer.service';
import {IncidentInfoWindowContentComponent} from '../incident-info-window-content/incident-info-window-content.component';
import { BreadcrumbsManagerService } from '../../../../data/breadcrumbs/breadcrumbs-manager.service';
import { VehicleBreadcrumb, VehicleBreadcrumbUpdate } from '../../../models/vehicle-breadcrumb';
import {
  BreadcrumbInfoWindowContentComponent
} from '../breadcrumb-info-window-content/breadcrumb-info-window-content.component';
import { ConfigurationModel } from '../../../models/configuration.model';

@Injectable({
  providedIn: 'root'
})
export class PopupInfoService {

  static readonly BREADCRUMB_INFO_WINDOW_SOURCE = 'breadcrumb-info-window';

  private map: Map;
  private configuration: ConfigurationModel;
  private lastComponentRef: ComponentRef<any>;
  private lastPopup: Popup;
  private marker: Marker = null;
  public viewContainerRef: ViewContainerRef; // initialized in app.component

  private readonly openSubscriptions = Array<Subscription>();

  constructor(private appRef: ApplicationRef,
              private observationsManagerService: ObservationsManagerService,
              private breadcrumbsManager: BreadcrumbsManagerService,
  ) { }

  init(map: Map, configuration: ConfigurationModel) {
    if (!!this.map) {
      throw Error('The map has already been set.');
    }
    this.map = map;
    this.configuration = configuration;
    this.connectToManager();
  }

  public closeAllPopups() {
    this.dismissLastInfoWindow();
  }

  private dismissLastInfoWindow() {
    if (!!this.lastComponentRef) {
      this.lastComponentRef.destroy();
      this.lastComponentRef = null;
    }
    if (!!this.lastPopup) {
      this.lastPopup.remove();
      this.lastPopup = null;
    }
    if (!!this.marker) {
      this.marker.remove();
      this.marker = null;
    }
  }

  private instantiateComponent<T>(componentType: Type<T>): ComponentRef<T> {
    const componentRef = this.viewContainerRef.createComponent(componentType);
    componentRef.onDestroy(() => {
      this.appRef.detachView(componentRef.hostView);
    });
    this.lastComponentRef = componentRef;

    return componentRef;
  }

  private showObservation(observation: Observation) {
    const infoComponentRef = this.instantiateComponent(ObservationInfoWindowContentComponent);
    infoComponentRef.setInput('observation', observation);
    infoComponentRef.setInput('isLiveMap', true);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({offset: [0, -15], className: 'marker-popup', maxWidth: '300px'})
      .setLngLat([observation.location.longitude, observation.location.latitude])
      .setDOMContent(div)
      .addTo(this.map);
  }

  private handleObservationSelectionFilterChanged(filter: ObservationFilterUpdate|null) {
    this.dismissLastInfoWindow();

    if (!!filter && !!filter.observation) {
      this.showObservation(filter.observation);
    }
  }

  public showRoadStatusInfoWindow(roadSegmentId: number, coords: LngLatLike) {
    this.dismissLastInfoWindow();
    const infoComponentRef = this.instantiateComponent(RoadStatusInfoWindowContentComponent);
    infoComponentRef.setInput('roadSegmentId', roadSegmentId);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({offset: [0, -15], className: 'marker-popup', maxWidth: '300px'})
        .setLngLat(coords)
        .setDOMContent(div)
        .addTo(this.map);
  }

  public showWeatherAlert(location: LngLat, oid: string, title: string) {
    this.dismissLastInfoWindow();
    const infoComponentRef = this.instantiateComponent(AlertInfoWindowContentComponent);
    infoComponentRef.setInput('oid', oid);
    infoComponentRef.setInput('title', title);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({maxWidth: '500px'})
        .setLngLat(location)
        .setDOMContent(div)
        .addTo(this.map);
  }

  public showTrafficIncidentInfo(location: LngLat, incidentInfo: IncidentInfo) {
    this.dismissLastInfoWindow();
    const infoComponentRef = this.instantiateComponent(IncidentInfoWindowContentComponent);
    infoComponentRef.setInput('incidentInfo', incidentInfo);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({maxWidth: '450px'})
        .setLngLat(location)
        .setDOMContent(div)
        .addTo(this.map);
  }

  private showBreadcrumbInfo(breadcrumbs: Array<VehicleBreadcrumb>) {
    const infoComponentRef = this.instantiateComponent(BreadcrumbInfoWindowContentComponent);
    infoComponentRef.setInput('breadcrumbs', breadcrumbs);
    infoComponentRef.setInput('isLiveMap', true);
    infoComponentRef.setInput('configuration', this.configuration);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    const coordinates = [breadcrumbs[0].location.longitude, breadcrumbs[0].location.latitude] as LngLatLike;

    this.lastPopup = new Popup({
      offset: [0, -15],
      className: 'marker-popup',
      maxWidth: '300px',
    })
      .setLngLat(coordinates)
      .setDOMContent(div)
      .addTo(this.map);

    this.lastPopup.on('close', () => {
      this.breadcrumbsManager.clearBreadcrumbSelection(PopupInfoService.BREADCRUMB_INFO_WINDOW_SOURCE);
      this.marker?.remove();
    });

    // create DOM element for the marker
    const markerElement = document.createElement('div');
    markerElement.id = 'marker';
    this.marker = new Marker({
      className: 'breadcrumb-found',
      element: markerElement,
    })
      .setLngLat(coordinates)
      .addTo(this.map);

    // adjust position of the popup if selection next or previous
    infoComponentRef.instance.visibleBreadcrumbChanged.subscribe((vehicleBreadcrumb) => {
      const newCoordinates = [vehicleBreadcrumb.location.longitude, vehicleBreadcrumb.location.latitude] as LngLatLike;
      this.lastPopup.setLngLat(newCoordinates);
      this.marker?.setLngLat(newCoordinates);
    });
  }

  private handleVehicleBreadcrumbSelectionChanged(breadcrumbSelectionUpdate: VehicleBreadcrumbUpdate) {
    this.dismissLastInfoWindow();
    if (!breadcrumbSelectionUpdate.breadcrumbs || breadcrumbSelectionUpdate.breadcrumbs.length === 0) {
      return;
    }

    this.showBreadcrumbInfo(breadcrumbSelectionUpdate.breadcrumbs);
  }

  private connectToManager() {
    const activeObservationSubscription = this.observationsManagerService.highlightedObservation$.subscribe(activeObservation => {
      this.handleObservationSelectionFilterChanged(activeObservation);
    });
    this.openSubscriptions.push(activeObservationSubscription);

    const selectedBreadcrumbSubscription = this.breadcrumbsManager.selectedBreadcrumbObservable.subscribe(breadcrumbSelectionUpdate => {
      this.handleVehicleBreadcrumbSelectionChanged(breadcrumbSelectionUpdate);
    });
    this.openSubscriptions.push(selectedBreadcrumbSubscription);
  }

  release() {
    if (!this.map) {
      throw Error('The map has not been set. Only a single map instance is supported.');
    }
    this.openSubscriptions.forEach(subscription => subscription?.unsubscribe());

    this.map = null;
    this.configuration = null;
    this.lastComponentRef = undefined;
    this.lastPopup = null;
    this.marker = null;
  }
}
